import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getDocs, doc, setDoc, query, where, getDoc, limit, serverTimestamp, runTransaction } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { sanitizeTeamName, sanitizeMatchType, sanitizeRegion } from '../utils/inputSanitizer';
import { addCSRFToken, validateFormCSRF } from '../utils/csrfProtection';
import { createMatchGroupId } from '../utils/matchGroupIdGenerator';
import { buildMirrorSubmissionData } from '../utils/mirrorSubmissionBuilder';
import { createMirrorPair, fetchMirrorSnapshot, markRecordAsOrphan } from '../utils/mirrorSyncService';
import { deleteEnemyStatsWithMirror } from '../utils/mirrorDeleteHandler';
import { validateMirrorPair } from '../utils/mirrorValidation';
import { clearPlayerStatsSeasonCache } from '../utils/playerStatsSessionCache';

const LINKED_NOTICE_PREF_KEY = 'enemy_stats_hide_linked_submission_notice';

export const useMirrorSubmission = ({
  db,
  teamId,
  seasonId,
  modeIdFromQuery,
  formRef,
  formState,
  applyEditData,
  onResetScores,
  onAfterDelete,
  onNavigateExisting,
  setPreviewData,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mirrorEnabled, setMirrorEnabled] = useState(false);
  const [mirrorConfirmed, setMirrorConfirmed] = useState(false);
  const [showMirrorConfirmModal, setShowMirrorConfirmModal] = useState(false);
  const [showMirrorEditModal, setShowMirrorEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [mirrorId, setMirrorId] = useState(null);
  const [mirrorExists, setMirrorExists] = useState(false);
  const [mirrorMissing, setMirrorMissing] = useState(false);
  const [matchGroupId, setMatchGroupId] = useState('');
  const [recordStatus, setRecordStatus] = useState('STANDALONE');
  const [lastKnownBaseUpdatedAt, setLastKnownBaseUpdatedAt] = useState(null);
  const [lastKnownMirrorUpdatedAt, setLastKnownMirrorUpdatedAt] = useState(null);
  const [staleMirrorWarning, setStaleMirrorWarning] = useState('');
  const [staleBaseWarning, setStaleBaseWarning] = useState('');
  const [orphanWarning, setOrphanWarning] = useState('');
  const [linkedNoticeType, setLinkedNoticeType] = useState('mirror');
  const [hideLinkedSubmissionNotice, setHideLinkedSubmissionNotice] = useState(() => {
    try {
      return localStorage.getItem(LINKED_NOTICE_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  const mirrorSyncRef = useRef(false);

  const toMillis = (value) => {
    if (!value) return null;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    return null;
  };

  const isStale = (latestMillis, lastKnownMillis) => (
    typeof latestMillis === 'number' &&
    typeof lastKnownMillis === 'number' &&
    latestMillis > lastKnownMillis
  );

  const flipMatchResult = (result) => {
    const normalized = String(result || '').trim().toUpperCase();
    if (normalized === 'WIN' || normalized === 'W') return 'Lose';
    if (normalized === 'LOSE' || normalized === 'LOSS' || normalized === 'L') return 'Win';
    return result;
  };

  const getYearFromDate = (value) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return String(parsed.getFullYear());
  };

  const persistHideLinkedNotice = (value) => {
    setHideLinkedSubmissionNotice(value);
    try {
      localStorage.setItem(LINKED_NOTICE_PREF_KEY, value ? '1' : '0');
    } catch {
      // Ignore storage errors.
    }
  };

  const openLinkedNotice = useCallback((type) => {
    setLinkedNoticeType(type || 'mirror');
    if (!hideLinkedSubmissionNotice) {
      setShowMirrorEditModal(true);
    }
  }, [hideLinkedSubmissionNotice]);

  const enemyStatsDocRef = useCallback(() => (
    doc(db, `teams/${teamId}/seasons/${seasonId}/database/enemyStats`)
  ), [db, teamId, seasonId]);

  const enemyStatsCollection = useCallback(() => (
    collection(enemyStatsDocRef(), 'modes')
  ), [enemyStatsDocRef]);

  const createEnemyStatsRef = useCallback(() => doc(enemyStatsCollection()), [enemyStatsCollection]);

  const enemyStatsByIdRef = useCallback((modeId) => doc(enemyStatsCollection(), modeId), [enemyStatsCollection]);

  const matchGroupMarkerRef = useCallback((groupId) => (
    doc(collection(enemyStatsDocRef(), 'matchGroups'), groupId)
  ), [enemyStatsDocRef]);

  useEffect(() => {
    const loadExisting = async () => {
      if (!teamId || !modeIdFromQuery || !seasonId) return;
      const ref = enemyStatsByIdRef(modeIdFromQuery);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        toast.error('Could not load enemy stat for editing.');
        return;
      }
      const data = snap.data() || {};
      setIsEdit(true);
      applyEditData(data);
      setStaleMirrorWarning('');
      setStaleBaseWarning('');
      setOrphanWarning('');
      setLastKnownBaseUpdatedAt(toMillis(data.updatedAt));
      const existingMirrorId = data.mirrorId || null;
      const existingMatchGroupId = data.matchGroupId || '';
      const existingStatus = data.recordStatus || (existingMirrorId ? 'LINKED' : 'STANDALONE');
      setMirrorId(existingMirrorId);
      setMatchGroupId(existingMatchGroupId);
      setRecordStatus(existingStatus);
      setMirrorMissing(false);
      setMirrorExists(false);
      const isTeamSummaryLinked =
        data.source === 'teamSummaryReciprocal' &&
        data.sourceCollection === 'teamStats' &&
        typeof data.sourceModeId === 'string' &&
        data.sourceModeId.length > 0;

      if (existingMirrorId) {
        const mirrorSnap = await fetchMirrorSnapshot({
          db,
          teamId,
          seasonId,
          mirrorId: existingMirrorId
        });
        const baseDocInfo = { id: modeIdFromQuery, data };
        const mirrorDocInfo = mirrorSnap.exists ? { id: existingMirrorId, data: mirrorSnap.data } : null;

        if (mirrorSnap.exists) {
          setLastKnownMirrorUpdatedAt(toMillis(mirrorSnap.data.updatedAt));
          const validation = validateMirrorPair(baseDocInfo, mirrorDocInfo, seasonId, teamId);
          if (validation.ok || validation.suggestedAction === 'WARN_ONLY') {
            setMirrorExists(true);
            openLinkedNotice('mirror');
            setRecordStatus('LINKED');
            if (!validation.ok) {
              toast.warn('Mirror link needs an upgrade. Save to apply fixes.');
            }
          } else {
            setMirrorMissing(true);
            setRecordStatus('ORPHAN');
            setMirrorId(null);
            setOrphanWarning('Linked record missing; this submission is now orphaned.');
            toast.warn('Mirror link invalid; marked as orphan.');
            try {
              await markRecordAsOrphan({ db, teamId, seasonId, modeId: modeIdFromQuery });
            } catch (err) {
              console.error('Failed to mark orphan record:', err);
            }
          }
        } else {
          setMirrorMissing(true);
          setRecordStatus('ORPHAN');
          setMirrorId(null);
          setOrphanWarning('Linked record missing; this submission is now orphaned.');
          try {
            await markRecordAsOrphan({ db, teamId, seasonId, modeId: modeIdFromQuery });
          } catch (err) {
            console.error('Failed to mark orphan record:', err);
          }
        }
      } else if (isTeamSummaryLinked) {
        openLinkedNotice('teamSummary');
      }
    };
    loadExisting();
  }, [teamId, seasonId, modeIdFromQuery, db, applyEditData, enemyStatsByIdRef, openLinkedNotice]);

  const buildBaseSubmissionData = () => {
    const form = formRef.current;
    const formData = new FormData(form);
    const data = {
      ...Object.fromEntries(formData.entries())
    };
    data.Date = formState.matchDate;
    data.Mode = formState.mode;
    data.MatchType = formState.matchType;
    data.MatchTypeId = formState.matchTypeId || '';
    data.MatchCategory = formState.matchCategory || '';
    data.MatchTypeRegion = formState.matchTypeRegion || '';
    data.Team = formState.teamName?.toUpperCase();
    data.TeamRegion = formState.teamRegion?.toUpperCase();
    data.EnemyRegion = formState.region;
    data.EnemyTeam = formState.enemyTeam;
    data.PF = formState.pointsFor;
    data.PA = formState.pointsAgainst;
    data.WinLose = formState.matchResult;
    data.Map = formState.selectedMap;
    data.PF = parseInt(data.PF || '0', 10);
    data.PA = parseInt(data.PA || '0', 10);
    return data;
  };

  const sanitizeSubmissionData = (data) => {
    const sanitized = { ...data };
    sanitized.EnemyTeam = sanitizeTeamName(sanitized.EnemyTeam);
    sanitized.MatchType = sanitizeMatchType(sanitized.MatchType);
    sanitized.MatchTypeId = typeof sanitized.MatchTypeId === 'string' ? sanitized.MatchTypeId.trim() : '';
    sanitized.MatchCategory = typeof sanitized.MatchCategory === 'string' ? sanitized.MatchCategory.trim().toUpperCase() : '';
    sanitized.MatchTypeRegion = sanitizeRegion(sanitized.MatchTypeRegion);
    sanitized.EnemyRegion = sanitizeRegion(sanitized.EnemyRegion);
    sanitized.Team = sanitizeTeamName(sanitized.Team);
    sanitized.TeamRegion = sanitizeRegion(sanitized.TeamRegion);
    return sanitized;
  };

  const findExistingByMatchGroupId = async (groupId) => {
    if (!groupId || !teamId || !seasonId) return null;
    const q = query(
      enemyStatsCollection(),
      where('matchGroupId', '==', groupId),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0];
  };

  const wrapWithCSRF = (data) => addCSRFToken({
    action: 'submit_team_mode_summary',
    ...data,
  });

  const submitForm = async ({ skipMirrorConfirm = false } = {}) => {
    if (isSubmitting) return;
    setStaleMirrorWarning('');
    setStaleBaseWarning('');
    setOrphanWarning('');
    if (!isEdit && mirrorEnabled && !mirrorConfirmed && !skipMirrorConfirm) {
      setShowMirrorConfirmModal(true);
      return;
    }
    if (!isEdit) {
      const check = rateLimiter.checkLimit('SUBMIT_ENEMY_STATS');
      if (!check.allowed) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_ENEMY_STATS'));
        toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
        return;
      }
      if (!rateLimiter.record('SUBMIT_ENEMY_STATS')) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_ENEMY_STATS'));
        toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
        return;
      }
    }

    setIsSubmitting(true);
    const baseData = buildBaseSubmissionData();
    if (setPreviewData) {
      setPreviewData(baseData);
    }
    const sanitizedBase = sanitizeSubmissionData(baseData);

    const effectiveMatchGroupId = matchGroupId || createMatchGroupId(seasonId);
    if (!matchGroupId) {
      setMatchGroupId(effectiveMatchGroupId);
    }

    const shouldCreateMirror = !isEdit && mirrorEnabled;
    const shouldCheckMirror = isEdit && mirrorId;
    const baseRef = isEdit && modeIdFromQuery
      ? enemyStatsByIdRef(modeIdFromQuery)
      : createEnemyStatsRef();
    const baseModeId = baseRef.id;

    let mirrorRef = null;
    let mirrorModeId = null;
    let sanitizedMirror = null;
    if (shouldCreateMirror || shouldCheckMirror) {
      const mirrorData = buildMirrorSubmissionData(baseData);
      sanitizedMirror = sanitizeSubmissionData(mirrorData);
      mirrorRef = shouldCreateMirror
        ? createEnemyStatsRef()
        : enemyStatsByIdRef(mirrorId);
      mirrorModeId = mirrorRef.id;
    }

    let effectiveRecordStatus = mirrorExists
      ? 'LINKED'
      : mirrorMissing
        ? 'ORPHAN'
        : shouldCreateMirror
          ? 'LINKED'
          : recordStatus === 'ORPHAN'
            ? 'ORPHAN'
            : 'STANDALONE';

    let effectiveMirrorId = shouldCreateMirror
      ? mirrorModeId
      : mirrorExists
        ? mirrorId
        : null;

    const basePayload = wrapWithCSRF({
      ...sanitizedBase,
      teamId,
      seasonId,
      matchGroupId: effectiveMatchGroupId,
      mirrorId: effectiveMirrorId,
      recordStatus: effectiveRecordStatus,
      ModeID: baseModeId,
      updatedAt: serverTimestamp(),
      ...(isEdit ? {} : { createdAt: serverTimestamp() })
    });

    const mirrorPayload = (shouldCreateMirror || shouldCheckMirror) && sanitizedMirror
      ? wrapWithCSRF({
        ...sanitizedMirror,
        teamId,
        seasonId,
        matchGroupId: effectiveMatchGroupId,
        mirrorId: baseModeId,
        recordStatus: 'LINKED',
        ModeID: mirrorModeId,
        updatedAt: serverTimestamp(),
        ...(isEdit ? {} : { createdAt: serverTimestamp() })
      })
      : null;

    if (!validateFormCSRF(basePayload) || (mirrorPayload && !validateFormCSRF(mirrorPayload))) {
      toast.error('Security validation failed. Please refresh and try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const { _csrf: baseCsrf, ...baseToSave } = basePayload;
      const writePayload = { ...baseToSave, ModeID: baseModeId };
      const mirrorWritePayload = mirrorPayload
        ? (() => {
          const { _csrf: mirrorCsrf, ...mirrorToSave } = mirrorPayload;
          return { ...mirrorToSave, ModeID: mirrorModeId };
        })()
        : null;

      if (!isEdit) {
        const lockResult = await runTransaction(db, async (tx) => {
          const markerRef = matchGroupMarkerRef(effectiveMatchGroupId);
          const snap = await tx.get(markerRef);
          if (snap.exists()) {
            return { duplicate: true };
          }
          tx.set(markerRef, {
            createdAt: serverTimestamp(),
            teamId,
            seasonId,
            matchGroupId: effectiveMatchGroupId
          });
          return { duplicate: false };
        });

        if (lockResult.duplicate) {
          const existing = await findExistingByMatchGroupId(effectiveMatchGroupId);
          toast.info('This match was already submitted.');
          setIsSubmitting(false);
          if (existing && typeof onNavigateExisting === 'function') {
            onNavigateExisting(existing.id);
          } else {
            toast.warn('Existing submission not found. Please contact support.');
          }
          return;
        }
      }

      if (shouldCreateMirror && mirrorModeId && mirrorPayload && mirrorRef && mirrorWritePayload) {
        await createMirrorPair({
          db,
          teamId,
          seasonId,
          baseModeId,
          mirrorModeId,
          baseWritePayload: writePayload,
          mirrorWritePayload
        });

        const verifyBaseSnap = await getDoc(baseRef);
        const verifyMirrorSnap = await getDoc(mirrorRef);
        if (!verifyBaseSnap.exists() || !verifyMirrorSnap.exists()) {
          toast.error('Mirror pair verification failed. Please retry.');
        } else {
          const validation = validateMirrorPair(
            { id: baseModeId, data: verifyBaseSnap.data() },
            { id: mirrorModeId, data: verifyMirrorSnap.data() },
            seasonId,
            teamId
          );
          if (!validation.ok && validation.suggestedAction !== 'WARN_ONLY') {
            toast.error('Mirror link verification failed. Please retry.');
          }
        }

        toast.success(`Match saved as ${baseModeId} + ${mirrorModeId}`);
      } else if (isEdit) {
        const allowMirrorSync = !!(shouldCheckMirror && mirrorExists && mirrorRef && mirrorWritePayload && !mirrorSyncRef.current);
        if (allowMirrorSync) {
          mirrorSyncRef.current = true;
        }
        let result;
        try {
          result = await runTransaction(db, async (tx) => {
            const baseSnap = await tx.get(baseRef);
            if (!baseSnap.exists()) {
              throw new Error('BASE_MISSING');
            }
            const baseData = baseSnap.data() || {};
            const baseUpdatedAtMillis = toMillis(baseData.updatedAt);
            const baseHasUpdatedAt = !!baseData.updatedAt;
            if (baseHasUpdatedAt && isStale(baseUpdatedAtMillis, lastKnownBaseUpdatedAt)) {
              throw new Error('BASE_STALE');
            }

            let recordStatusForUpdate = effectiveRecordStatus;
            let mirrorIdForUpdate = effectiveMirrorId;
            let mirrorSyncStatus = 'NONE';
            let mirrorDocExists = false;
            let mirrorLegacy = false;
            let mirrorData = null;

            if (shouldCheckMirror && mirrorRef) {
              const mirrorSnap = await tx.get(mirrorRef);
              mirrorDocExists = mirrorSnap.exists();
              if (!mirrorDocExists) {
                mirrorSyncStatus = 'MISSING';
                recordStatusForUpdate = 'ORPHAN';
                mirrorIdForUpdate = null;
              } else {
                mirrorData = mirrorSnap.data() || {};
                const validation = validateMirrorPair(
                  { id: baseModeId, data: baseData },
                  { id: mirrorModeId, data: mirrorData },
                  seasonId,
                  teamId
                );
                if (!validation.ok && validation.suggestedAction !== 'WARN_ONLY') {
                  mirrorSyncStatus = 'INVALID';
                  recordStatusForUpdate = 'ORPHAN';
                  mirrorIdForUpdate = null;
                } else {
                  const mirrorHasUpdatedAt = !!mirrorData.updatedAt;
                  if (!baseHasUpdatedAt || !mirrorHasUpdatedAt) {
                    mirrorLegacy = true;
                    mirrorSyncStatus = 'LEGACY';
                  } else if (isStale(toMillis(mirrorData.updatedAt), lastKnownMirrorUpdatedAt)) {
                    mirrorSyncStatus = 'STALE';
                  } else if (allowMirrorSync) {
                    mirrorSyncStatus = 'SYNCED';
                  } else {
                    mirrorSyncStatus = 'SKIPPED';
                  }
                }
              }
            }

            const baseUpgrade = {};
            if (!baseData.matchGroupId) baseUpgrade.matchGroupId = effectiveMatchGroupId;
            if (!baseData.recordStatus) baseUpgrade.recordStatus = recordStatusForUpdate;
            if (!baseData.createdAt) baseUpgrade.createdAt = serverTimestamp();
            if (!baseData.mirrorId && mirrorIdForUpdate) baseUpgrade.mirrorId = mirrorIdForUpdate;

            tx.update(baseRef, {
              ...writePayload,
              mirrorId: mirrorIdForUpdate,
              recordStatus: recordStatusForUpdate,
              updatedAt: serverTimestamp(),
              ...baseUpgrade
            });

            if (mirrorSyncStatus === 'SYNCED' && mirrorRef && mirrorWritePayload) {
              const mirrorUpgrade = {};
              if (mirrorData) {
                if (!mirrorData.matchGroupId) mirrorUpgrade.matchGroupId = effectiveMatchGroupId;
                if (!mirrorData.recordStatus) mirrorUpgrade.recordStatus = 'LINKED';
                if (!mirrorData.createdAt) mirrorUpgrade.createdAt = serverTimestamp();
                if (!mirrorData.mirrorId) mirrorUpgrade.mirrorId = baseModeId;
              }
              tx.update(mirrorRef, {
                ...mirrorWritePayload,
                updatedAt: serverTimestamp(),
                ...mirrorUpgrade
              });
            }

            let teamSummarySyncStatus = 'NONE';
            const sourceModeId = typeof baseData.sourceModeId === 'string' ? baseData.sourceModeId : '';
            const sourceSeasonId = typeof baseData.sourceSeasonId === 'string' ? baseData.sourceSeasonId : '';
            const shouldSyncBackToTeamSummary =
              baseData.source === 'teamSummaryReciprocal' &&
              baseData.sourceCollection === 'teamStats' &&
              !!sourceModeId;

            if (shouldSyncBackToTeamSummary) {
              const seasonCandidates = [
                sourceSeasonId,
                seasonId,
                baseData.SeasonId,
                getYearFromDate(baseData.Date)
              ]
                .map((value) => String(value || '').trim())
                .filter(Boolean);

              let linkedTeamRef = null;
              for (const seasonKey of [...new Set(seasonCandidates)]) {
                const candidateRef = doc(db, `teams/${teamId}/seasons/${seasonKey}/database/teamStats/modes/${sourceModeId}`);
                const candidateSnap = await tx.get(candidateRef);
                if (candidateSnap.exists()) {
                  linkedTeamRef = candidateRef;
                  break;
                }
              }

              if (!linkedTeamRef) {
                const legacyRef = doc(db, `teams/${teamId}/database/teamStats/modes/${sourceModeId}`);
                const legacySnap = await tx.get(legacyRef);
                if (legacySnap.exists()) {
                  linkedTeamRef = legacyRef;
                }
              }

              if (linkedTeamRef) {
                tx.update(linkedTeamRef, {
                  Date: writePayload.Date,
                  Mode: writePayload.Mode,
                  MatchType: writePayload.MatchType,
                  MatchTypeId: writePayload.MatchTypeId || '',
                  MatchCategory: writePayload.MatchCategory || '',
                  MatchTypeRegion: writePayload.MatchTypeRegion || '',
                  Map: writePayload.Map,
                  EnemyTeam: writePayload.Team,
                  EnemyRegion: writePayload.TeamRegion,
                  PF: Number(writePayload.PA) || 0,
                  PA: Number(writePayload.PF) || 0,
                  WinLose: flipMatchResult(writePayload.WinLose),
                  Notes: writePayload.Notes || '',
                  ReciprocalEnemyModeID: baseModeId,
                  ReciprocalEnemySeasonID: seasonId,
                  updatedAt: serverTimestamp()
                });
                teamSummarySyncStatus = 'SYNCED';
              } else {
                teamSummarySyncStatus = 'MISSING';
              }
            }

            return {
              recordStatus: recordStatusForUpdate,
              mirrorId: mirrorIdForUpdate,
              mirrorSyncStatus,
              mirrorDocExists,
              baseLegacy: !baseHasUpdatedAt,
              mirrorLegacy,
              teamSummarySyncStatus
            };
          });
        } finally {
          if (allowMirrorSync) {
            mirrorSyncRef.current = false;
          }
        }

        if (result) {
          if (result.mirrorSyncStatus === 'MISSING' || result.mirrorSyncStatus === 'INVALID') {
            setMirrorMissing(true);
            setMirrorExists(false);
            setMirrorId(null);
            setRecordStatus('ORPHAN');
            setOrphanWarning('Linked record missing; this submission is now orphaned. Changes will not sync.');
          } else {
            if (shouldCheckMirror) {
              setMirrorExists(result.mirrorDocExists);
              setMirrorMissing(!result.mirrorDocExists);
            }
            if (result.mirrorId) {
              setMirrorId(result.mirrorId);
            }
            if (result.recordStatus) {
              setRecordStatus(result.recordStatus);
            }
          }

          if ((result.baseLegacy || result.mirrorLegacy) && shouldCheckMirror) {
            setStaleMirrorWarning('Linked record lacks version info; sync is disabled until upgraded.');
          } else if (result.mirrorSyncStatus === 'STALE') {
            setStaleMirrorWarning('Linked record updated elsewhere; mirror sync skipped.');
          }

          if (result.mirrorSyncStatus === 'SYNCED') {
            setLastKnownMirrorUpdatedAt(Date.now());
          }

          if (result.teamSummarySyncStatus === 'MISSING') {
            toast.warn('Linked team summary not found. Enemy record was updated only.');
          }
        }

        toast.success(`Match updated (${baseModeId})`);
      } else {
        await setDoc(baseRef, writePayload);
        toast.success(`Match saved as ${baseModeId}`);
      }

      setLastKnownBaseUpdatedAt(Date.now());
      clearPlayerStatsSeasonCache(teamId, seasonId);
      if (onResetScores) {
        onResetScores();
      }
      if (!isEdit) {
        setMatchGroupId('');
      }
      if (shouldCreateMirror) {
        setMirrorEnabled(false);
        setMirrorConfirmed(false);
      }
      if (!isEdit && effectiveRecordStatus === 'ORPHAN') {
        setMirrorId(null);
        setMirrorExists(false);
        setMirrorMissing(true);
        setRecordStatus('ORPHAN');
      }
    } catch (err) {
      if (err && err.message === 'BASE_STALE') {
        setStaleBaseWarning('This record was updated elsewhere. Refresh before saving.');
        toast.warn('Update blocked: record changed since you opened it.');
      } else if (err && err.message === 'BASE_MISSING') {
        toast.error('This record no longer exists.');
      } else {
        console.error('Error saving to Firestore:', err);
        toast.error('Submission failed.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm();
  };

  const handleDelete = async () => {
    if (!teamId || !modeIdFromQuery || !seasonId || isDeleting) return;
    setIsDeleting(true);
    try {
      let canDeleteMirror = mirrorExists;
      if (mirrorId) {
        const baseRef = enemyStatsByIdRef(modeIdFromQuery);
        const mirrorRef = enemyStatsByIdRef(mirrorId);
        const baseSnap = await getDoc(baseRef);
        const mirrorSnap = await getDoc(mirrorRef);
        if (baseSnap.exists() && mirrorSnap.exists()) {
          const validation = validateMirrorPair(
            { id: modeIdFromQuery, data: baseSnap.data() },
            { id: mirrorId, data: mirrorSnap.data() },
            seasonId,
            teamId
          );
          canDeleteMirror = validation.ok ||
            validation.suggestedAction === 'WARN_ONLY' ||
            mirrorSnap.data().mirrorId === modeIdFromQuery;
          if (!validation.ok && validation.suggestedAction !== 'WARN_ONLY') {
            toast.warn('Mirror link invalid; deleting current record only.');
          }
        } else {
          canDeleteMirror = false;
        }
      }

      await deleteEnemyStatsWithMirror({
        db,
        teamId,
        seasonId,
        modeId: modeIdFromQuery,
        mirrorId,
        mirrorExists: canDeleteMirror
      });
      clearPlayerStatsSeasonCache(teamId, seasonId);
      toast.success('Submission deleted');
      if (onAfterDelete) {
        onAfterDelete();
      }
    } catch (err) {
      console.error('Error deleting submission:', err);
      toast.error('Delete failed.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmModal(false);
    }
  };

  const handleMirrorToggle = (value) => {
    setMirrorEnabled(value);
    setMirrorConfirmed(false);
  };

  const confirmMirror = () => {
    setMirrorConfirmed(true);
    setShowMirrorConfirmModal(false);
    submitForm({ skipMirrorConfirm: true });
  };

  const cancelMirror = () => {
    setMirrorConfirmed(false);
    setShowMirrorConfirmModal(false);
  };

  return {
    isEdit,
    isSubmitting,
    isDeleting,
    mirrorEnabled,
    recordStatus,
    staleMirrorWarning,
    staleBaseWarning,
    orphanWarning,
    linkedNoticeType,
    hideLinkedSubmissionNotice,
    showMirrorConfirmModal,
    showMirrorEditModal,
    showDeleteConfirmModal,
    handleSubmit,
    handleDelete,
    openDeleteConfirm: () => setShowDeleteConfirmModal(true),
    cancelDelete: () => setShowDeleteConfirmModal(false),
    closeMirrorEdit: () => setShowMirrorEditModal(false),
    setHideLinkedSubmissionNotice: persistHideLinkedNotice,
    confirmMirror,
    cancelMirror,
    setMirrorEnabled: handleMirrorToggle,
  };
};
